import { useEffect, useState } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  FormTextInputV2,
  FormNumberInputV2,
  FormUploadImageInputV2,
  FormDatePickerV2Input,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  ProductGroupsActions,
  useProductDispatch,
  useProductSelector,
} from '@wajeez-web/stores/product';
import { RadioGroup } from '@wajeez-web/components/mui';

type Input = Server.CreateProductGroupInput & {
  id?: number;
};

interface ProductGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void | Promise<void>;
  id?: number;
}

export const ProductGroupModal = ({
  isOpen,
  onClose,
  onSubmit,
  id,
}: ProductGroupModalProps) => {
  const productDispatch = useProductDispatch();

  const [formValues, setFormValues] = useState<Input>();

  const createProductGroupStatus = useProductSelector(
    ({ productGroups }) => productGroups.createProductGroup.status
  );
  const editProductGroupStatus = useProductSelector(
    ({ productGroups }) => productGroups.editProductGroup.status
  );
  const productGroupById = useProductSelector(
    ({ productGroups }) => productGroups.productGroupById
  );

  const { handleSubmit, control, reset, setValue, watch } = useForm<Input>({
    defaultValues: {},
  });

  watch((data) => {
    setFormValues({ ...data, countries: [] });
  });

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    let res: any;

    const mappedData: Input = {
      ...data,
      domainWeight: data.domainWeight ? Number(data.domainWeight) : undefined,
    };

    if (!id) {
      res = await productDispatch(
        ProductGroupsActions.createProductGroupAsync({
          ...mappedData,
        })
      );
    } else {
      res = await productDispatch(
        ProductGroupsActions.editProductGroupAsync({
          id,
          ...data,
        })
      );
    }

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit && (await onSubmit());
    }
  };

  useEffect(() => {
    if (productGroupById.data) {
      reset(productGroupById.data);
    }
  }, [productGroupById.data]);

  useEffect(() => {
    if (id) {
      productDispatch(ProductGroupsActions.getProductGroupByIdAsync(id));
    }

    return () => {
      productDispatch(ProductGroupsActions.clearProductGroup());
    };
  }, [id, productDispatch]);

  const isLoading = productGroupById.status === 'loading';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Product Group"
      alertTarget="modal"
      fontClassName="font-[Tajawal]"
      withScroll
    >
      <div className="w-full">
        {isLoading ? (
          <div className="w-full min-h-[200px] flex justify-center items-center">
            <Spin />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            className="w-full flex flex-col justify-start items-start"
          >
            <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
              <FormTextInputV2
                name="groupName"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  fieldOptions: {},
                  required: true,
                  label: 'Group Name',
                  placeHolder: 'Group Name',
                }}
                required
              />
              <FormTextInputV2
                name="domainName"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  fieldOptions: {},
                  label: 'Domain Name',
                  placeHolder: 'Domain Name',
                }}
              />
              <FormNumberInputV2
                name="domainWeight"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.Number,
                  fieldOptions: { min: -1 },
                  label: 'Domain Weight',
                  placeHolder: 'Domain Weight',
                }}
              />
              <div className="w-full">
                <FormDatePickerV2Input
                  name="startsFrom"
                  control={control}
                  fieldMeta={{
                    fieldType: Server.FieldType.Date,
                    fieldOptions: {},
                    label: 'Starts From',
                    placeHolder: 'Starts From',
                  }}
                />
              </div>
              <div className="w-full">
                <FormDatePickerV2Input
                  name="endsAt"
                  control={control}
                  fieldMeta={{
                    fieldType: Server.FieldType.Date,
                    fieldOptions: {},
                    label: 'Ends At',
                    placeHolder: 'Ends At',
                  }}
                />
              </div>
              <FormUploadImageInputV2
                name="bannerImageReference"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.UploadImage,
                  fieldOptions: {},
                  label: 'Banner Image Reference',
                }}
              />
              <FormTextInputV2
                name="country"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  fieldOptions: { minLength: 2, maxLength: 2 },
                  label: 'Country',
                  placeHolder: 'Country',
                  onChange: (value) => {
                    setValue('country', value.toUpperCase());
                  },
                }}
                validationErrorMessages={{
                  maxLength: 'This field should contain 2 characters',
                  minLength: 'This field should contain 2 characters',
                }}
              />
              <RadioGroup
                options={[
                  { value: 'isDefault', label: 'افتراضي' },
                  { value: 'isGifts', label: 'هدايا' },
                  { value: 'isFree', label: 'مجاني' },
                  { value: '', label: 'بلا نوع' },
                ]}
                onChange={(value) => {
                  switch (value) {
                    case 'isDefault':
                      setValue('isDefault', true);
                      setValue('isGifts', false);
                      setValue('isFree', false);

                      break;
                    case 'isGifts':
                      setValue('isGifts', true);
                      setValue('isDefault', false);
                      setValue('isFree', false);

                      break;
                    case 'isFree':
                      setValue('isFree', true);
                      setValue('isDefault', false);
                      setValue('isGifts', false);

                      break;
                    case '':
                      setValue('isFree', false);
                      setValue('isDefault', false);
                      setValue('isGifts', false);

                      break;
                    default:
                      break;
                  }
                }}
                value={
                  formValues?.isDefault
                    ? 'isDefault'
                    : formValues?.isGifts
                    ? 'isGifts'
                    : formValues?.isFree
                    ? 'isFree'
                    : ''
                }
                defaultValue=""
                label="Type"
              />
            </div>
            <div className="mt-6 w-full flex justify-end items-center space-s-2">
              <div className="w-[70px]">
                <ButtonHeadless
                  bgStyle="bg-warmGray-300"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                  action={onClose}
                >
                  <div className="text-warmGray-600 text-[16px] font-medium">
                    <ReturnTranslatedText
                      text="buttons.Cancel"
                      origintext="Cancel"
                    />
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[70px]">
                <ButtonHeadless
                  type="submit"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text="buttons.Save"
                      origintext="Save"
                    />
                    {(createProductGroupStatus === 'loading' ||
                      editProductGroupStatus === 'loading') && (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
